// src/contexts/UserContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { handleJSAPIAccess, handleUserAuth, isCacheExpired, setUserCachedData, getUserCachedData, clearUserCachedData, getUserCacheKey } from '../utils/auth_access_util';
import axios from 'axios';
import { API_CONFIG, loadAppConfig, refreshAppConfig } from '../config/apiConfig';

// 创建用户上下文
export const UserContext = createContext();

// 【H6 修复】给回调式异步函数加超时保护：超时后 resolve(null) 强制继续，
// 避免 handleUserAuth / handleJSAPIAccess 的回调在个别 WebView 环境中永不触发时，
// initUserInfo 永久卡在 await → loading 永为 true → 首页骨架屏白屏。
const callWithTimeout = (executor, ms, label) => {
    return new Promise((resolve) => {
        let settled = false;
        let timer = null;
        const done = (val) => {
            if (settled) return;          // 超时后回调晚到则忽略
            settled = true;
            if (timer) clearTimeout(timer);
            resolve(val);
        };
        timer = setTimeout(() => {
            console.warn(`[Auth] ${label} ${ms}ms 未完成，超时强制继续`);
            done(null);
        }, ms);
        try {
            executor(done);
        } catch (e) {
            console.error(`[Auth] ${label} 同步异常:`, e);
            done(null);
        }
    });
};

// 上下文提供者组件
export const UserProvider = ({ children }) => {
    const [userInfo, setUserInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isDriver, setIsDriver] = useState(false);
    const [isDirectDispatch, setIsDirectDispatch] = useState(false);
    const [checkingAdmin, setCheckingAdmin] = useState(false);
    const [checkingDriver, setCheckingDriver] = useState(false);
    const [checkingDirectDispatch, setCheckingDirectDispatch] = useState(false);
    // 【新功能体验】备用状态：名单中的用户可在飞书端看到新功能模块（灰度测试用）
    const [isNewFeatureExperience, setIsNewFeatureExperience] = useState(false);
    const [checkingNewFeatureExperience, setCheckingNewFeatureExperience] = useState(false);
    const [degradedMode, setDegradedMode] = useState(false);
    const [permissionHint, setPermissionHint] = useState(null);
    const [isBound, setIsBound] = useState(true); // 默认已绑定

    // 【多租户改造】应用启动时加载租户配置
    useEffect(() => {
        loadAppConfig().then(() => {
            console.log('[多租户] 租户配置加载完成');
        }).catch(err => {
            console.error('[多租户] 租户配置加载失败:', err);
        });
    }, []);

    // 获取用户详细信息
    const fetchUserDetailInfo = async (accessToken, openId) => {
        try {
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/feiShu/getUserInfo`, {
                apiToken: API_CONFIG.apiToken,
                userAccessToken: accessToken,
                userId: openId,
                companyId: API_CONFIG.companyId,
                tenantKey: API_CONFIG.tenantKey || localStorage.getItem('tenantKey') || '',
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.data && response.data.code === 200) {
                let userDetail = response.data.data;
                if (typeof userDetail === 'string') {
                    try {
                        userDetail = JSON.parse(userDetail);
                    } catch (error) {
                        console.error('解析用户信息失败:', error);
                        throw new Error('用户信息格式错误');
                    }
                }

                // ⚠️ 2026-08-04 临时关闭解绑状态分支：让所有用户能正常登录（绑定/解绑校验后续重新设计）
                // 原逻辑：后端返回空 open_id + unmatched_reason → 视为已解绑 → 进入未绑定状态
                // 现在：后端已不再返回解绑标记（getUserInfo 直接返回真实用户信息），此分支不会触发
                // 保留代码但增加 open_id 兜底判断：即使 open_id 为空也按正常用户处理
                if (userDetail && userDetail.open_id === '' && userDetail.unmatched_reason && false) {
                    console.warn('[Unbind] 用户已解绑:', userDetail.unmatched_reason);
                    setIsBound(false);
                    // 返回最小用户信息，不缓存
                    return {
                        open_id: '',
                        name: userDetail.name || '',
                        mobile: userDetail.mobile || '',
                        unmatched_reason: userDetail.unmatched_reason,
                        is_unbound: true
                    };
                }

                // G16: 检测降级模式
                if (userDetail && userDetail.degradedMode === true) {
                    setDegradedMode(true);
                    setPermissionHint(userDetail.permissionHint || null);
                    console.warn('[G16] 检测到降级模式:', userDetail.permissionHint);
                } else {
                    setDegradedMode(false);
                    setPermissionHint(null);
                }
                let userInfo = userDetail.user || userDetail;
                // 确保 userInfo 包含 employee_no 字段
                if (!userInfo.employee_no && userDetail.employee_no) {
                    userInfo.employee_no = userDetail.employee_no;
                }
                // 确保 userInfo 包含 avatar_url 字段
                if (!userInfo.avatar_url) {
                    // 从 avatar 对象中提取头像 URL
                    if (userInfo.avatar && userInfo.avatar.avatar_240) {
                        // 移除头像 URL 中的反引号
                        userInfo.avatar_url = userInfo.avatar.avatar_240.replace(/`/g, '').trim();
                    } else if (userInfo.avatar && userInfo.avatar.avatar_640) {
                        // 移除头像 URL 中的反引号
                        userInfo.avatar_url = userInfo.avatar.avatar_640.replace(/`/g, '').trim();
                    } else if (userInfo.avatar && userInfo.avatar.avatar_72) {
                        // 移除头像 URL 中的反引号
                        userInfo.avatar_url = userInfo.avatar.avatar_72.replace(/`/g, '').trim();
                    } else if (userInfo.avatar && userInfo.avatar.avatar_origin) {
                        // 移除头像 URL 中的反引号
                        userInfo.avatar_url = userInfo.avatar.avatar_origin.replace(/`/g, '').trim();
                    } else {
                        // 如果没有头像 URL，生成一个基于用户名的默认头像
                        userInfo.avatar_url = `https://ui-avatars.com/api/?name=${encodeURIComponent(userInfo.name || '用户')}&background=random&color=fff`;
                    }
                }
                // 处理部门信息 - 兼容多种可能的数据结构
                let deptName = '';

                // 尝试从 department_path 获取部门名称
                if (userInfo.department_path && userInfo.department_path.length > 0) {
                    const deptPath = userInfo.department_path[0];
                    console.log('部门路径数据:', deptPath);

                    // 尝试多种可能的字段结构
                    deptName =
                        deptPath.department_name?.name ||    // 结构1: department_name.name
                        deptPath.name ||                      // 结构2: 直接是name字段
                        deptPath.department_name ||           // 结构3: department_name是字符串
                        deptPath.dept_name ||                 // 结构4: dept_name字段
                        '';

                    console.log('从department_path提取的部门名称:', deptName);
                }

                // 如果 department_path 没有获取到，尝试从其他字段获取
                if (!deptName) {
                    deptName =
                        userInfo.department ||                // 字段: department
                        userInfo.department_name ||           // 字段: department_name
                        userInfo.dept_name ||                 // 字段: dept_name
                        '';

                    if (deptName) {
                        console.log('从其他字段提取的部门名称:', deptName);
                    } else {
                        console.log('没有部门路径信息，部门名称为空');
                    }
                }
                console.log('用户信息11111:', userInfo);
                userInfo.department_name = deptName;
                return userInfo;
            } else {
                throw new Error('获取用户详细信息失败: ' + (response.data.message || '未知错误'));
            }
        } catch (error) {
            console.error('获取用户详细信息失败:', error);
            throw error;
        }
    };

    // 检查是否是管理员（ISV模式：按open_id校验租户配置）
    const checkAdminStatus = async (userId) => {
        try {
            // 首先检查localStorage中是否已经存储了管理员状态
            const storedAdminStatus = getUserCachedData(userId, 'isAdmin');
            if (storedAdminStatus !== null) {
                // 使用存储的管理员状态
                setIsAdmin(storedAdminStatus === 'true');
                setCheckingAdmin(false);
                return;
            }

            // 如果没有存储，调用接口检查
            setCheckingAdmin(true);
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/checkAdminByCompanyId`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantKey: API_CONFIG.tenantKey || localStorage.getItem('tenantKey') || '',
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                applicantId: userId    // 用 open_id 校验
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const adminStatus = response.data && response.data.code === 200;
            setIsAdmin(adminStatus);
            // 存储管理员状态到localStorage
            setUserCachedData(userId, 'isAdmin', adminStatus.toString());
        } catch (error) {
            console.error('检查管理员身份失败，保持缓存状态:', error);
            // 保持当前状态，不设置为false
            // 这样即使接口调用失败，也不会丢失权限
        } finally {
            setCheckingAdmin(false);
        }
    };

    // 检查是否是司机（ISV模式：按open_id校验租户配置）
    const checkDriverStatus = async (userId) => {
        try {
            // 首先检查localStorage中是否已经存储了司机状态
            const storedDriverStatus = getUserCachedData(userId, 'isDriver');
            if (storedDriverStatus !== null) {
                // 使用存储的司机状态
                setIsDriver(storedDriverStatus === 'true');
                setCheckingDriver(false);
                return;
            }

            // 如果没有存储，调用接口检查
            setCheckingDriver(true);
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/checkDriverByCompanyId`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantKey: API_CONFIG.tenantKey || localStorage.getItem('tenantKey') || '',
                tenantId: API_CONFIG.id || localStorage.getItem('tenantId') || undefined,
                applicantId: userId    // 用 open_id 校验
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const driverStatus = response.data && response.data.code === 200;
            setIsDriver(driverStatus);
            // 存储司机状态到localStorage
            setUserCachedData(userId, 'isDriver', driverStatus.toString());
        } catch (error) {
            console.error('检查司机身份失败，保持缓存状态:', error);
            // 保持当前状态，不设置为false
            // 这样即使接口调用失败，也不会丢失权限
        } finally {
            setCheckingDriver(false);
        }
    };

    // 检查是否有直接派车权限（ISV模式：管理员 或 租户配置了直接派车权限的用户）
    const checkDirectDispatchStatus = async (userId) => {
        try {
            // 首先检查localStorage中是否已经存储了直接派车状态
            const storedDirectDispatchStatus = getUserCachedData(userId, 'isDirectDispatch');
            if (storedDirectDispatchStatus !== null) {
                // 使用存储的直接派车状态
                setIsDirectDispatch(storedDirectDispatchStatus === 'true');
                setCheckingDirectDispatch(false);
                return;
            }

            // 如果没有存储，调用接口检查
            setCheckingDirectDispatch(true);
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/checkDirectDispatchByCompanyId`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantKey: API_CONFIG.tenantKey || localStorage.getItem('tenantKey') || '',   // 与 companyId 同时传，后端复合精确匹配（试用企业共享 companyId）
                applicantId: userId    // 用 open_id 校验
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const directDispatchStatus = response.data && response.data.code === 200;
            setIsDirectDispatch(directDispatchStatus);
            // 存储直接派车状态到localStorage
            setUserCachedData(userId, 'isDirectDispatch', directDispatchStatus.toString());
        } catch (error) {
            console.error('检查直接派车权限失败，保持缓存状态:', error);
            // 保持当前状态，不设置为false
            // 这样即使接口调用失败，也不会丢失权限
        } finally {
            setCheckingDirectDispatch(false);
        }
    };

    // 检查是否有新功能体验权限（仅租户配置 new_feature_experience_open_ids 白名单内的用户可见）
    // ⚠️ 备用方法：新功能模块开发好后，在入口组件中调用它，命中白名单才显示入口
    //    参考直接派车 checkDirectDispatchStatus 实现，独立于现有权限链路，不改动直接派车逻辑
    const checkNewFeatureExperienceStatus = async (userId) => {
        try {
            // 关键修复：进入函数立即设置 checking=true，避免页面守卫 useEffect 在异步校验前误判为"不在名单"
            setCheckingNewFeatureExperience(true);
            // 先检查localStorage中是否已缓存新功能体验状态
            const storedStatus = getUserCachedData(userId, 'isNewFeatureExperience');
            if (storedStatus !== null) {
                setIsNewFeatureExperience(storedStatus === 'true');
                setCheckingNewFeatureExperience(false);
                return storedStatus === 'true';
            }

            // 未缓存则调用接口校验
            setCheckingNewFeatureExperience(true);
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/checkNewFeatureExperienceByCompanyId`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantKey: API_CONFIG.tenantKey || localStorage.getItem('tenantKey') || '',   // 与 companyId 同时传，后端复合精确匹配（试用企业共享 companyId）
                applicantId: userId    // 用 open_id 校验
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const expStatus = response.data && response.data.code === 200;
            setIsNewFeatureExperience(expStatus);
            // 缓存新功能体验状态到localStorage
            setUserCachedData(userId, 'isNewFeatureExperience', expStatus.toString());
            return expStatus;
        } catch (error) {
            console.error('检查新功能体验权限失败，保持缓存状态:', error);
            return false;
        } finally {
            setCheckingNewFeatureExperience(false);
        }
    };

    // 一次查询用户全部角色权限（管理员/司机/直接派车），单接口单缓存
    // force=true 时跳过缓存强制查询（设置页"刷新用户信息"使用，保证权限即时生效）
    // 新接口失败时（如后端未升级）降级到旧三个接口，保证功能可用
    const checkUserPermissions = async (userId, force = false) => {
        try {
            // 非强制刷新时，先检查localStorage缓存：命中且未过期（24h）则直接使用
            if (!force) {
                const permKey = getUserCacheKey(userId, 'userPermissions');
                const cachedPermissions = getUserCachedData(userId, 'userPermissions');
                if (cachedPermissions && !isCacheExpired(permKey)) {
                    try {
                        const perms = JSON.parse(cachedPermissions);
                        setIsAdmin(perms.isAdmin === true);
                        setIsDriver(perms.isDriver === true);
                        setIsDirectDispatch(perms.isDirectDispatch === true);
                        setCheckingAdmin(false);
                        setCheckingDriver(false);
                        setCheckingDirectDispatch(false);
                        return;
                    } catch (e) {
                        console.warn('解析权限缓存失败，回退接口查询:', e);
                    }
                }
            }

            setCheckingAdmin(true);
            setCheckingDriver(true);
            setCheckingDirectDispatch(true);
            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/carApplication/checkUserPermissionsByCompanyId`, {
                apiToken: API_CONFIG.apiToken,
                companyId: API_CONFIG.companyId,
                tenantKey: API_CONFIG.tenantKey || localStorage.getItem('tenantKey') || '',   // 与 companyId 同时传，后端复合精确匹配（试用企业共享 companyId）
                applicantId: userId    // 用 open_id 校验
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const isOk = response.data && response.data.code === 200;
            const data = isOk ? (response.data.data || {}) : {};
            const isAdminRes = data.isAdmin === true;
            const isDriverRes = data.isDriver === true;
            const isDirectDispatchRes = data.isDirectDispatch === true;

            setIsAdmin(isAdminRes);
            setIsDriver(isDriverRes);
            setIsDirectDispatch(isDirectDispatchRes);
            // 缓存整体权限对象到localStorage（24h，与旧单角色缓存行为一致）
            setUserCachedData(userId, 'userPermissions', JSON.stringify({
                isAdmin: isAdminRes,
                isDriver: isDriverRes,
                isDirectDispatch: isDirectDispatchRes
            }));
        } catch (error) {
            console.error('查询用户权限失败，降级到旧接口逐个查询:', error);
            // 降级：后端未升级（404）或网络异常时，回退到旧三个接口逐个校验
            await Promise.allSettled([
                checkAdminStatus(userId),
                checkDriverStatus(userId),
                checkDirectDispatchStatus(userId)
            ]);
        } finally {
            setCheckingAdmin(false);
            setCheckingDriver(false);
            setCheckingDirectDispatch(false);
        }
    };

    // H2: 初始化用户信息（并行化改造）
    const initUserInfo = async () => {
        try {
            setLoading(true);
            setError(null);

            // H2 Step 1: 先检查localStorage缓存（同步，即时可用）
            const storedUserInfo = localStorage.getItem('userInfo');
            let cachedUser = null;
            if (storedUserInfo) {
                try {
                    const parsed = JSON.parse(storedUserInfo);
                    const userId = parsed ? (parsed.open_id || parsed.user_id) : null;
                    if (parsed && userId) {
                        cachedUser = parsed;
                    }
                } catch (parseError) {
                    console.error('解析localStorage中的用户信息失败:', parseError);
                    localStorage.removeItem('userInfo');
                }
            }

            // H2 Step 2: 先执行用户免登，获取 tenant_key 并让后端写入 Session
            //            然后再执行 JSAPI 鉴权（鉴权需要 tenant_access_token，依赖 Session 中的租户配置）
            // 【H6】8s 超时兜底：个别 WebView 中 h5sdk.ready/requestAuthCode 回调不触发时不再永久等待
            const userInfoData = await callWithTimeout((done) => {
                handleUserAuth((data) => done(data || null));
            }, 8000, 'handleUserAuth');

            // H2 Step 2.5: 用户免登成功后，再进行 JSAPI 鉴权
            if (userInfoData && userInfoData.access_token) {
                await callWithTimeout((done) => {
                    handleJSAPIAccess((isSuccess) => {
                        if (!isSuccess) {
                            console.warn('JSAPI鉴权失败，但用户已登录，继续加载应用');
                        }
                        done(isSuccess);
                    });
                }, 6000, 'handleJSAPIAccess');
            } else {
                console.warn('用户免登失败，从本地缓存加载配置');
                await loadAppConfig();
                await callWithTimeout((done) => {
                    handleJSAPIAccess((isSuccess) => {
                        done(isSuccess);
                    });
                }, 6000, 'handleJSAPIAccess');
            }

            // H2 Step 3: 用户数据就绪，立即渲染（如果有缓存先用缓存）
            if (cachedUser) {
                console.log('[H2] 使用缓存数据立即渲染');
                setUserInfo(cachedUser);
                setLoading(false);
                // 后台静默更新
               /* if (cachedUser.mobile) {
                    const mobile = cachedUser.mobile.replace(/^\+86/, '');
                    const userId = cachedUser.open_id || cachedUser.user_id;
                    checkAdminStatus(userId);
                    checkDriverStatus(userId);
                }*/
                const userId = cachedUser.open_id || cachedUser.user_id;
                checkUserPermissions(userId);
            }

            // H2 Step 4: 用户免登完成，获取最新用户数据
            if (userInfoData) {
                // 免登成功后刷新租户配置（传 tenant_key 参数，确保能拿到真实配置）
                await refreshAppConfig(userInfoData.tenant_key)
                console.log('[多租户] 已刷新租户配置: companyId=' + API_CONFIG.companyId + ', tenantKey=' + API_CONFIG.tenantKey)
                try {
                    const detailedUserInfo = await fetchUserDetailInfo(userInfoData.access_token, userInfoData.open_id);
                    
                    // 检测解绑状态：不缓存，不检查权限（⚠️ 2026-08-04 临时关闭，后端不再返回 is_unbound）
                    if (detailedUserInfo && detailedUserInfo.is_unbound && false) {
                        console.log('[Unbind] 用户已解绑，显示未绑定状态');
                        localStorage.removeItem('userInfo');
                        setUserInfo(detailedUserInfo);
                        setIsBound(false);
                        setLoading(false);
                        return;
                    }

                    // 更新缓存
                    const userId = userInfoData.open_id || userInfoData.user_id;
                    localStorage.setItem('userInfo', JSON.stringify(detailedUserInfo));
                    setUserCachedData(userId, 'userInfo', detailedUserInfo);

                    // 用最新数据更新UI
                    setUserInfo(detailedUserInfo);
                    // 检查是否是管理员和司机
                   /* if (detailedUserInfo.mobile) {
                        const mobile = detailedUserInfo.mobile.replace(/^\+86/, '');
                        checkAdminStatus(userId);
                        checkDriverStatus(userId);
                    }*/
                    checkUserPermissions(userId);
                } catch (error) {
                    console.error('获取详细用户信息失败，保留现有部门信息:', error);
                    // G16: 检测权限降级场景
                    if (error.message && (
                        error.message.includes('99991663') ||
                        error.message.includes('99991664') ||
                        error.message.includes('99991665')
                    )) {
                        setDegradedMode(true);
                        setPermissionHint('通讯录权限未开启，部分功能受限。请联系企业管理员在飞书后台开启通讯录权限。');
                    }
                    // 解绑场景：fetchUserDetailInfo 返回了 is_unbound 数据（未抛异常），走到这里的是真正异常
                    if (userInfo && (userInfo.department_name || userInfo.department)) {
                        userInfoData.department_name = userInfo.department_name || userInfo.department;
                    }
                    // 如果 isBound 为 false（解绑状态），跳过设置 userInfo
                    if (!isBound) {
                        return;
                    }
                    setUserInfo(userInfoData);
                  /*  if (userInfoData.mobile) {
                        const mobile = userInfoData.mobile.replace(/^\+86/, '');
                        const userId = userInfoData.open_id || userInfoData.user_id;
                        checkAdminStatus(userId);
                        checkDriverStatus(userId);
                    }*/
                    const userId = userInfoData.open_id || userInfoData.user_id;
                    checkUserPermissions(userId);
                }
            } else if (!cachedUser) {
                // 既无缓存也无鉴权结果：降级输出
                console.log('未获取到用户信息，尝试使用缓存数据');
                const fallbackCached = localStorage.getItem('userInfo');
                if (fallbackCached) {
                    try {
                        const parsed = JSON.parse(fallbackCached);
                        const userId = parsed.open_id || parsed.user_id;
                        if (userId) {
                            console.log('使用缓存的用户信息:', parsed);
                            setUserInfo(parsed);
                            /*if (parsed.mobile) {
                                const mobile = parsed.mobile.replace(/^\+86/, '');
                                checkAdminStatus(userId);
                                checkDriverStatus(userId);
                            }*/
                            checkUserPermissions(userId);
                            return;
                        }
                    } catch (e) {
                        console.error('解析缓存用户信息失败:', e);
                    }
                }
                throw new Error('未获取到用户信息');
            }
        } catch (error) {
            console.error('初始化用户信息失败:', error);
            setError(error.message);
            // 使用默认数据
            setUserInfo({
                name: '未知用户',
                mobile: '',
                user_id: '',
                open_id: '',
                department: ''
            });
        } finally {
            setLoading(false);
        }
    };




    // 初始化时获取用户信息
    useEffect(() => {
        initUserInfo();
    }, []);

    // 轻量刷新：只拉最新用户信息和权限，不复用免登
    // forcePermissions=true 时强制重新查询权限（绕过缓存），供设置页"刷新用户信息"使用
    const refreshUserInfoLight = async (forcePermissions = false) => {
        try {
            setLoading(true);
            const userId = userInfo?.open_id || userInfo?.user_id || '';
            const accessToken = localStorage.getItem('lk_token') || '';
            if (!userId || !accessToken) {
                console.warn('[Refresh] 无可用登录态，降级为完整刷新');
                initUserInfo();
                return;
            }
            // 1. 从后端获取最新用户信息（复用 session）
            const detailedUserInfo = await fetchUserDetailInfo(accessToken, userId);
            // 2. 更新 localStorage 缓存
            localStorage.setItem('userInfo', JSON.stringify(detailedUserInfo));
            setUserCachedData(userId, 'userInfo', detailedUserInfo);
            // 3. 检测解绑状态（⚠️ 2026-08-04 临时关闭，后端不再返回 is_unbound）
            if (detailedUserInfo && detailedUserInfo.is_unbound && false) {
                setIsBound(false);
                setUserInfo(detailedUserInfo);
                setLoading(false);
                return;
            }
            // 4. 更新 UI
            setUserInfo(detailedUserInfo);
            // 5. 刷新权限（设置页强制刷新时绕过缓存）
            await checkUserPermissions(userId, forcePermissions);
            setLoading(false);
            console.log('[Refresh] 用户信息已更新');
        } catch (error) {
            console.error('[Refresh] 更新用户信息失败:', error);
            setLoading(false);
        }
    };

    return (
        <UserContext.Provider value={{ 
            userInfo, 
            loading, 
            error, 
            isAdmin, 
            isDriver, 
            isDirectDispatch,
            checkingAdmin, 
            checkingDriver,
            checkingDirectDispatch,
            // 【新功能体验】备用：isNewFeatureExperience + 校验方法（新功能模块入口用）
            isNewFeatureExperience,
            checkingNewFeatureExperience,
            checkNewFeatureExperienceStatus,
            degradedMode,
            permissionHint,
            isBound,
            refreshUserInfo: initUserInfo,       // 完整刷新（免登+鉴权）
            refreshUserInfoLight                 // 轻量刷新（只拉用户信息+权限）
        }}>
            {children}
        </UserContext.Provider>
    );
};

// 自定义Hook，方便使用用户上下文
export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};